import type { ElementType, ReactNode } from 'react'
import React, { createContext, useEffect, useMemo } from 'react'

import Typography from '../../Atoms/Typography'
import Placeholder from '../../Atoms/Placeholder'
import { Action } from './Action'
import { Address } from './Address'
import { Body } from './Body'
import { Details } from './Details'
import { Footer } from './Footer'
import { Header } from './Header'
import { Images } from './Images'
import { Info } from './Info'
import { Main } from './Main'
import { Title } from './Title'
import { Tag } from './Tag'

import styles from './index.module.css'
import { type Matches, useContainerQueryFallback } from '@s4l/utils/hooks/useContainerQueryFallback'

export const breakpoints = {
  small: 375,
  medium: 600,
  large: 800,
}

export type ComponentWithDisplayName = React.ComponentType<any> & { displayName?: string }

export type CardVariant = 'default' | 'fixed-height' | 'lite'

interface ICardProps<E extends ElementType = 'a'> {
  children: ReactNode
  featuredText?: string
  featured?: boolean
  url: string
  variant?: CardVariant
  asLink: E
  onContainerQueryChange?: (matches: Matches) => void
  onClick?: () => void
}

export interface ICardContext {
  url?: string
  Link: ElementType | string
  size?: 's' | 'm' | 'l' | string
  variant?: CardVariant
  featured?: boolean
  featuredText?: string
  isPlaceholder?: boolean
  matches: Matches
  onClick?: () => void
}
export const CardContext = createContext<ICardContext>({
  url: '',
  Link: 'a',
  size: 's',
  variant: 'default',
  featured: false,
  featuredText: undefined,
  isPlaceholder: false,
  matches: {
    small: false,
    medium: false,
    large: false,
  },
})

function Card<E extends ElementType = 'a'>(props: ICardProps<E>) {
  const {
    variant = 'default',
    featuredText,
    featured = false,
    url,
    children,
    asLink = 'a',
    onContainerQueryChange,
    onClick,
    ...rest
  } = props

  const { containerRef, matches } = useContainerQueryFallback(breakpoints)

  // eslint-disable-next-line no-nested-ternary
  const dataSize = matches.large ? 'l' : matches.medium ? 'm' : 's'

  useEffect(() => {
    onContainerQueryChange?.(matches)
  }, [onContainerQueryChange, matches])

  const contextValue = useMemo(
    () => ({
      url,
      Link: asLink as ElementType,
      size: dataSize as ICardContext['size'],
      variant,
      featured: featured || !!featuredText,
      featuredText,
      matches,
      isPlaceholder: false,
      onClick,
    }),
    [url, asLink, dataSize, variant, featuredText, featured, matches, onClick]
  )

  return (
    <CardContext.Provider value={contextValue}>
      {featuredText && (
        <div className={styles.featured}>
          <Typography variant="caption" size="l" bold>
            {featuredText}
          </Typography>
        </div>
      )}
      <article
        ref={containerRef}
        className={styles.container}
        data-variant={variant}
        data-feature-text={featured || !!featuredText}
        data-has-featured-text={!!featuredText}
        data-size={dataSize}
        {...rest}
      >
        {children}
      </article>
    </CardContext.Provider>
  )
}

interface IPlaceholderProps {
  variant?: CardVariant
}

function CardPlaceholder(props: Readonly<IPlaceholderProps>) {
  const { variant = 'default' } = props

  const { containerRef, matches } = useContainerQueryFallback(breakpoints)

  // eslint-disable-next-line no-nested-ternary
  const dataSize = matches.large ?  'l' : // prettier-ignore
                   matches.medium ? 'm' : // prettier-ignore 
                   's' // prettier-ignore

  const contextValue = useMemo(
    () => ({
      url: '',
      Link: 'div' as ElementType,
      size: dataSize as ICardContext['size'],
      variant,
      featured: false,
      featuredText: undefined,
      matches,
      isPlaceholder: true,
    }),
    [dataSize, variant, matches]
  )

  return (
    <CardContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        data-variant={variant}
        data-size={dataSize}
        data-placeholder="true"
        className={styles.container}
      >
        <Header>
          <Images.Placeholder />
        </Header>
        <Body>
          <Main>
            <Title.Placeholder />
            <Placeholder width="48px" height="16px" rounded />
            <Address.Placeholder />
          </Main>
          <Details>
            <Placeholder width="200px" height="24px" rounded />
            <Tag.Placeholder />
            <Placeholder width="200px" height="24px" rounded />
          </Details>
          <Footer>
            <Placeholder width="120px" height="50px" rounded />
            <Placeholder width="80px" height="40px" rounded />
          </Footer>
        </Body>
        <Placeholder.Animation />
      </div>
    </CardContext.Provider>
  )
}

Card.Header = Header
Card.Action = Action
Card.Images = Images
Card.Body = Body
Card.Main = Main
Card.Details = Details
Card.Footer = Footer
Card.Title = Title
Card.Address = Address
Card.Info = Info
Card.Tag = Tag
Card.Placeholder = CardPlaceholder

export default Card
